import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGetHook from "../../customHooks/useGetHook";
import APIS from "../../constant/Endpoint";
import {
  patchApiData,
  patchApiFormData,
  postApiData,
} from "../../helpers/AxiosInstance";
import { toast } from "react-toastify";
import { tw } from "@careinfo/tailwind";
import Spinner from "../../components/Reusable/Spinner";
import loadable from "@loadable/component";
import { usePDFPrintHook } from "@careinfo/layout";
import PopupModal from "../../components/Reusable/Modal";
import Button from "../../components/Reusable/Button";

const EditDraftSalesComponent = loadable(
  () => import("../../components/Sales/EditDraftSales")
);

const EditDraftSales = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const queryClient = useQueryClient();
  const [isRevertLoading, setRevertLoading] = useState(false);
  const { id } = useParams();
  const { data, isLoading: isDataLoading } = useGetHook({
    queryKey: ["sales", `sales-${id}`],
    url: `${APIS.SALES_PURCHASE}${id}/`,
    params: {},
  });

  const { data: companyData, isLoading: isCompanyDataLoading } = useGetHook({
    url: APIS.COMPANY_PROFILE,
    queryKey: ["company-profile"],
    params: {},
  });

  const handleEdit = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = `${APIS.SALES_PURCHASE}${id}/`;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    formData.append("reference_number", values?.reference_number ?? "");
    values?.reference_date
      ? formData.append("reference_date", values?.reference_date)
      : null;
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);
    formData.append("party", values?.party);
    values?.bill_number
      ? formData.append("bill_number", values?.bill_number)
      : null;
    formData.append("tax_rate", values?.tax_rate);
    formData.append("taxable_amount", values?.taxable_amount);
    formData.append("total_amount", values?.total_amount);
    formData.append("discount", values?.total_discount);
    formData.append("tax_amount", values?.vat_tax_amount);
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    if (values?.items) {
      const cleanedItems = values.items.map(
        ({
          available_quantity,
          is_maintain_stock,
          is_taxable,
          last_purchase_price,
          unit,
          previous_quantity,
          serial_number,
          ...rest
        }) => ({
          ...rest,
          is_taxable: is_taxable ? "true" : "false",
        })
      );
      formData.append("items", JSON.stringify(cleanedItems));
    }
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    values?.narration ? formData.append("narration", values?.narration) : null;
    try {
      const res = await patchApiFormData({ url, formData, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["sales"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/sales");
        }
        toast.success("Updated Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleCancel = async () => {
  //   const url = `${APIS.BILL}${id}/cancel/`;
  //   const formData = {
  //     status: "Cancelled",
  //   };
  //   try {
  //     postApiData({ url, formData, setError: () => {} });
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };
  const handleCancel = async () => {
    const url = `${APIS.BILL}${id}/cancel/`;
    const formData = {
      status: "Cancelled",
    };

    try {
      const res = await postApiData({ url, formData, setError: () => {} });

      if (res?.status === 200 || res?.status === 201) {
        toast.success("Bill cancelled successfully!");
        queryClient.invalidateQueries({
          queryKey: ["sales"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while cancelling the bill.");
    }
  };
  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Sales Invoice",
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };
  const handleRevert = async (setError) => {
    const url = `${APIS.BILL}${id}/revert/`;
    setRevertLoading(true);
    try {
      const res = await postApiData({ url, formData: {}, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["sales"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevertLoading(false);
    }
  };
  const [openCancelBiLLPopup, setOpenCancelBillPopup] = useState(false);

  return (
    <>
      <div
        className={tw`w-full flex justify-between items-center py-2 px-2 mb-4 border-b border-gray-300`}
      >
        <h2 className={tw`text-[#5E5757] font-extrabold text-xl uppercase`}>
          Edit Draft Sales
        </h2>

        {data?.status !== "Cancelled" && data?.status !== "Draft" && (
          <div className={tw`flex items-center gap-3 w-[70px]`}>
            <Button
              type="button"
              label="Cancel"
              color="bg-red-600 py-1"
              isLoading={isLoading}
              onClick={() => setOpenCancelBillPopup(true)}
            />
          </div>
        )}
      </div>
      {isDataLoading || isCompanyDataLoading ? (
        <Spinner />
      ) : (
        <EditDraftSalesComponent
          handleEdit={handleEdit}
          isLoading={isLoading}
          data={data}
          openPrintPopup={openPrintPopup}
          companyData={companyData}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          isPrintLoading={isPrintLoading}
          handleRevert={handleRevert}
          handleCancel={handleCancel}
          isRevertLoading={isRevertLoading}
        />
      )}
      <PopupModal
        isOpen={openCancelBiLLPopup}
        setIsOpen={setOpenCancelBillPopup}
        title="Cancel Bill"
      >
        <div className={tw`p-4 mt-10`}>
          <p className={tw`text-gray-600 mb-6`}>
            Are you sure you want to cancel this bill? This action cannot be
            undone.
          </p>

          <div className={tw`flex justify-center gap-3`}>
            <button
              onClick={() => setOpenCancelBillPopup(false)}
              className={tw`px-6 py-2 rounded-md focus:outline-none bg-gray-200 text-gray-800 hover:bg-gray-300 transition`}
            >
              No
            </button>

            <button
              onClick={() => {
                // handleCancel();
                setOpenCancelBillPopup(false);
              }}
              className={tw`px-6 py-2 rounded-md  focus:outline-none bg-red-600 text-white hover:bg-red-700 transition`}
            >
              Yes
            </button>
          </div>
        </div>
      </PopupModal>
    </>
  );
};
export default EditDraftSales;

